import React, { useContext, useEffect, useState } from 'react';
import { BareUserNotification } from '@go-shared/types/userNotification';
import { getNotifications } from '@go-client/lib/api/userNotification';
import { BubbleOverlay } from '@go-client/components/BubbleUI';

export interface NotificationBundle {
  notifications: BareUserNotification[];
  refreshContext: () => Promise<void>;
}

const NOTIFICATIONS_POLLING_SECONDS = 30;
const NotificationContext = React.createContext<NotificationBundle | undefined>(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);

  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }

  return context;
}

export function WithNotifications(props: React.HTMLAttributes<HTMLDivElement>) {
  const { className: propClasses, children, ...rest } = props;

  const [tick, setTick] = useState(0);
  const [notificationBundle, setNotificationBundle] = useState<NotificationBundle | undefined>(
    undefined
  );

  // WARNING: If you would like to use state to further complex this refresh function,
  // see UserInfoContext.tsx for an example of how you can do this.
  async function fetchNotifications() {
    const notifs = await getNotifications('new');

    setNotificationBundle({
      notifications: notifs,
      refreshContext: fetchNotifications,
    });
  }

  useEffect(() => {
    const t = setInterval(() => {
      setTick(tck => (tck < 0 ? tck : tck + 1));
    }, 1000 * NOTIFICATIONS_POLLING_SECONDS);

    return () => {
      clearInterval(t);
      setTick(0);
    };
  }, []);

  useEffect(() => {
    if (tick < 0) return;

    fetchNotifications().then().catch(console.error);
  }, [tick]);

  const classes = ['h-full'];
  if (propClasses) classes.push(propClasses);

  return (
    <BubbleOverlay loading={!notificationBundle} {...rest} className={classes.join(' ')}>
      {notificationBundle && (
        <NotificationProvider notificationBundle={notificationBundle}>
          {props.children}
        </NotificationProvider>
      )}
    </BubbleOverlay>
  );
}

interface NotificationProviderPropsBase {
  notificationBundle: NotificationBundle;
}

type NotificationProviderProps = NotificationProviderPropsBase & { children?: React.ReactNode };

export default function NotificationProvider(props: NotificationProviderProps) {
  const { notificationBundle, children } = props;

  return (
    <NotificationContext.Provider value={notificationBundle}>
      {children}
    </NotificationContext.Provider>
  );
}
